import React, { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { clearImportBookmarksMessage } from "../../redux/slices/bookmarkSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import parse from "html-react-parser";
import { useOutletContext } from "react-router-dom";
import BookmarkGoogleResultContext from "../../components/bookmark/BookmarkGoogleResultContext";

const ImportBookmarks = () => {
  const { setUrlToBookmark, setWhichModalOpen } = useOutletContext();
  const dispatch = useDispatch();
  const [htmlContent, setHtmlContent] = useState("");
  const [errors, setError] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const { token } = useSelector((state) => state.auth);
  const { importError, importBookmarkMessage, loading } = useSelector(
    (state) => state.bookmark
  );
  const [contextMenu, setContextMenu] = useState(null);
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setHtmlContent(e.target.result);
      };
      reader.readAsText(file);
    }
  };
  useEffect(() => {
    if (htmlContent) {
      const links = document.querySelectorAll(".import-bookmarks-list a");

      // Right-Click Event Handler (Prevent default & show alert)
      const handleRightClick = (event) => {
        event.preventDefault();
        setContextMenu({
          x: event.clientX,
          y: event.clientY,
          record: { title: event.target.textContent, link: event.target.href }
        });
      };

      // Left-Click Event Handler (Open in New Tab)
      const handleLeftClick = (event) => {
        event.preventDefault();
        window.open(event.target.href, "_blank", "noopener,noreferrer");
      };

      // Attach events to all anchor tags
      links.forEach((link) => {
        link.addEventListener("contextmenu", handleRightClick);
        link.addEventListener("click", handleLeftClick);
      });

      // Cleanup event listeners
      return () => {
        links.forEach((link) => {
          link.removeEventListener("contextmenu", handleRightClick);
          link.removeEventListener("click", handleLeftClick);
        });
      };
    }
  }, [htmlContent]);

  const handleDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setUploadedFile(file);
      setError("");
    }
  };

  const handleRejected = (fileRejections) => {
    if (fileRejections.length > 0) {
      const rejected = fileRejections[0].file;
      setError(
        `Only .html files are allowed. You uploaded: ${rejected.name} (${(
          rejected.size / 1024
        ).toFixed(2)} KB)`
      );
      setUploadedFile(null);
    }
  };

  useEffect(() => {
    if (importError !== null) {
      setError(importError);
      toast.error(importError);
      setTimeout(() => {
        dispatch(clearImportBookmarksMessage());
        setError("");
        setUploadedFile(null);
      }, 1000);
    }
  }, [importError, dispatch]);

  useEffect(() => {
    if (importBookmarkMessage !== "") {
      toast.success(importBookmarkMessage);
      setUploadedFile(null);
    }
  }, [importBookmarkMessage]);

  const handleOptionClick = (option) => {
    contextMenu.record = { ...contextMenu.record, type: option };
    setUrlToBookmark(contextMenu);
    setContextMenu(null);
    setWhichModalOpen("newBookmark");
  };

  return (
    <div className="max-w-screen-xl mx-auto px-4 sm:px-6 xl:px-2 h-full">
      <div className="bg-white sm:rounded-tl-[20px] rounded-bl-[20px] rounded-br-[20px] p-4 xl:p-8 h-full">
        <div className="flex flex-wrap md:h-full w-full flex-col ">
          <Dropzone
            accept={{
              "text/html": [".html"]
            }}
            onDropRejected={handleRejected}
            onDrop={handleDrop}
          >
            {({ getRootProps, getInputProps }) => (
              <div className="w-full md:w-1/2 h-full flex flex-wrap flex-col space-y-4 pb-4 md:pb-0 md:pe-4">
                <div className="p-8 flex flex-col justify-between bg-white border border-dashed border-gray-300 rounded-xl  h-full items-center gap-4">
                  <div className="text-sm overflow-auto h-full">
                    <h1 className="font-medium text-lg leading-5 text-dark-blue mb-1">How to Export Your Existing Bookmarks from your Browser to save them selectively in ZillionLinX.</h1>
                    <p className="mb-3">(Right click on the bookmarks after importing them in the panel on the right)</p>
                    <h2 className="font-medium">Google Chrome</h2>
                    <ul className="list-decimal ps-5 mb-4">
                      <li>Open Chrome.</li>
                      <li>Click the 3-dot menu (top-right).</li>
                      <li>Go to Bookmarks &gt; Bookmark manager.</li>
                      <li>
                        Click the 3-dot menu in Bookmark Manager (top-right of
                        the manager window).
                      </li>
                      <li>Select Export bookmarks.</li>
                      <li>
                        Choose where to save the .html file and click Save.
                      </li>
                    </ul>
                    <h2 className="font-medium">Mozilla Firefox</h2>
                    <ul className="list-decimal ps-5 mb-4">
                      <li>Open Firefox.</li>
                      <li>Click the Library icon (or menu &gt; Bookmarks).</li>
                      <li>Choose Manage bookmarks (or press Ctrl+Shift+O).</li>
                      <li>Click the 3-dot menu in Bookmark Manager (top-right of the manager window).
                      </li>
                      <li>Choose Export Bookmarks to HTML....</li>
                      <li>Choose a location and click Save.</li>
                    </ul>
                    <h2 className="font-medium">Microsoft Edge</h2>
                    <ul className="list-decimal ps-5 mb-4">
                      <li>Open Edge.</li>
                      <li>Click the 3-dot menu (top-right).</li>
                      <li>Go to Favorites &gt; Manage favorites.</li>
                      <li>
                      In the upper-right corner, click the 3-dot menu in the Favorites pane.
                      </li>
                      <li>Select Export favorites.</li>
                      <li>
                      Save the .html file where you want.
                      </li>
                    </ul>
                    <h2 className="font-medium">Safari (Mac only)</h2>
                    <ul className="list-decimal ps-5 mb-4">
                      <li>Open Safari.</li>
                      <li>In the menu bar, click File &gt; Export &gt; Bookmarks.</li>
                      <li>Choose a save location and click Save.</li>
                      <li>The file will be named Bookmarks.html by default.</li>
                    </ul>
                  </div>
                  <div
                    className="cursor-pointer w-full p-6 flex justify-center bg-white border border-dashed border-gray-300 rounded-xl  items-center"
                    {...getRootProps()}
                  >
                    <input
                      {...getInputProps()}
                      accept=".html"
                      onChange={handleFileUpload}
                    />
                    <div className="text-center">
                      <span className="inline-flex justify-center items-center size-10 bg-gray-100 text-gray-800 rounded-full">
                        <svg
                          className="shrink-0 size-4"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="17 8 12 3 7 8"></polyline>
                          <line x1="12" x2="12" y1="3" y2="15"></line>
                        </svg>
                      </span>

                      <div className="mt-4 flex flex-wrap justify-center text-sm/6 text-gray-600">
                        <span className="pe-1 font-medium text-gray-800 ">
                          Drop your file here or
                        </span>
                        <span className="bg-white font-semibold text-blue-600 hover:text-blue-700 rounded-lg decoration-2 hover:underline focus-within:outline-hidden focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 ">
                          browse
                        </span>
                      </div>

                      <p className="mt-1 text-xs text-gray-400 ">
                        Pick a file up to 2MB.
                      </p>
                    </div>
                  </div>
                  {uploadedFile && errors === "" ? (
                    <div className="p-3 bg-white border border-solid border-gray-300 rounded-xl ">
                      <div className="mb-1 flex justify-between items-center">
                        <div className="flex items-center gap-x-3">
                          <span
                            className="size-10 flex justify-center items-center border border-gray-200 text-gray-500 rounded-lg "
                            data-hs-file-upload-file-icon=""
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="shrink-0 size-5"
                            >
                              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                            </svg>
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              <span className="truncate inline-block max-w-75 align-bottom">
                                {uploadedFile?.name}
                              </span>
                            </p>
                            {errors && (
                              <p className="text-xs text-red-500">{errors}</p>
                            )}

                            <p
                              className="text-xs text-gray-500 "
                              data-hs-file-upload-file-size=""
                            >
                              {!isNaN(uploadedFile?.size)
                                ? `${parseFloat(
                                    (uploadedFile?.size / 1024).toFixed(2)
                                  )}KB`
                                : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-x-2">
                          <button
                            type="button"
                            className="text-gray-500 hover:text-gray-800 focus:outline-hidden focus:text-gray-800 "
                            data-hs-file-upload-remove=""
                            onClick={() => setUploadedFile(null)}
                          >
                            <svg
                              className="shrink-0 size-4"
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                              <line x1="10" x2="10" y1="11" y2="17"></line>
                              <line x1="14" x2="14" y1="11" y2="17"></line>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </Dropzone>
          <div className="w-full md:w-1/2 md:ps-4 import-bookmarks-list min-h-[500px] md:min-h-full h-[500px] overflow-auto md:h-full">
            <div className="w-full p-12 pt-8 bg-white border border-dashed border-gray-300 rounded-xl h-full overflow-auto flex flex-wrap items-center">
              {htmlContent ? (
                parse(htmlContent)
              ) : (
                <img src="/no-data-concept.jpeg" alt="" />
              )}
            </div>
          </div>
          {/* <div className="flex flex-wrap justify-end">
                  <button
                  disabled={loading}
                    className="disabled:bg-light-blue disabled:text-dark-blue disabled:pointer-events-none btn dark-btn h-12 border-none"
                    onClick={importBookmark}
                  >
                    Import
                  </button>
                </div> */}
        </div>
        {contextMenu && (
          <BookmarkGoogleResultContext
            setContextMenu={setContextMenu}
            contextMenu={contextMenu}
            handleOptionClick={handleOptionClick}
          />
        )}
      </div>
    </div>
  );
};

export default ImportBookmarks;
